import PrimaryCtaButton from '@/components/layout/article/actions/PrimaryCtaButton';
import MediaTwoCol from '@/components/layout/MediaTwoCol';
import { Button } from '@/components/ui/Button';
import { transformContent } from '@/lib/helpers/content';
import { graphql, Link } from 'gatsby';
import orderBy from 'lodash/orderBy';
import { ArrowRight } from 'lucide-react';
import React from 'react';
import breadcrumbStructuredData from '../../../app/schema/breadcrumbStructuredData';
import { formatPageTitle } from '../../../app/utils/meta';
import { asyncTrackingEvent } from '../../../app/utils/tracking';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import Layout, { Meta } from '../../../components/layout/Layout';
import OrganisationHeadline from '../../../components/layout/organisation/OrganisationHeadline';
import { Article } from '../../../types/content';

/**
 * Renders out gradsims
 * @param {any} props
 * @return {any}
 */
const GradsimsTemplate = ({ data, pageContext, location }: any) => {
  const org = data.organisation;
  const pageType: string = 'Gradsims';
  const hasOpportunities = data.allOpportunity.nodes.length > 0;
  const meta: Meta[] = org.metatag
    .filter((t: any) => t.attributes?.name?.toLowerCase() === 'title')
    .map((metatag: any) => {
      return {
        tag: metatag.tag,
        content: formatPageTitle(`${pageType} - ${metatag.attributes.content}`),
        href: metatag.attributes.href,
        name: metatag.attributes.name,
        rel: metatag.attributes.rel
      };
    });
  meta.push({
    tag: 'meta',
    content: `${pageType} from ${org.title}`,
    name: 'description'
  });
  meta.push({
    tag: 'meta',
    content: `noindex`,
    name: 'robots'
  });

  const articles = orderBy(data.inPartnership.nodes, ['type', 'field_priority'], ['desc', 'desc']);

  const blocks = articles.map((content: any, i: number) => {
    return (
      <MediaTwoCol
        key={`headline-content-${i}`}
        bkgColor={'bg-white'}
        image={
          content?.relationships?.field_headline_image
            ? content.relationships.field_headline_image.localFile.childImageSharp.gatsbyImageData
            : data.headerBackground.childImageSharp.gatsbyImageData
        }
        imageAlt={content.title}
        mediaColClass="lg:w-7/12"
        contentColClass="lg:w-5/12 lg:flex lg:items-center"
        shadow={true}
        rounded={true}
      >
        <div className="space-y-4 lg:space-y-7">
          <div className="space-y-3">
            <h2 className="display-title-semibold display-title-xl lg:display-title-2xl">{content.title}</h2>
            {content.field_abstract && (
              <p className="leading-6 text-sm lg:text-base line-clamp-5">{content.field_abstract}</p>
            )}
            {content.field_primary_cta_link?.uri ? (
              <PrimaryCtaButton
                article={transformContent<Article>(content)}
                variant="bare"
                onClick={(e) => {
                  asyncTrackingEvent({
                    track: 'gradsim_click',
                    trackAbacus: 'gradsim_click',
                    trackId: content.id ? content.id : null,
                    trackText: content?.title ? content.title : null,
                    trackValue: content?.type ? content.type : null
                  });
                }}
              />
            ) : (
              <Button asChild variant="bare">
                <Link
                  to={content.path.alias}
                  onClick={(e) => {
                    asyncTrackingEvent({
                      track: 'gradsim_click',
                      trackAbacus: 'gradsim_click',
                      trackId: content.id ? content.id : null,
                      trackText: content?.title ? content.title : null,
                      trackValue: content?.type ? content.type : null
                    });
                  }}
                  data-cy={`link:view-content`}
                >
                  <ArrowRight className="w-4" />
                  <span>Find out more</span>
                </Link>
              </Button>
            )}
          </div>
        </div>
      </MediaTwoCol>
    );
  });

  return (
    <Layout
      meta={meta}
      bodyAttributes={{
        'data-track-content-id': org.drupal_id,
        'data-track-content-type': 'organisation'
      }}
      structuredData={[breadcrumbStructuredData(pageContext.crumbs)]}
    >
      <main>
        <Breadcrumb crumbs={pageContext.crumbs} width="2xl" bkgColor="" containerClass="py-3" />
        <OrganisationHeadline
          id={org.drupal_id}
          orgName={org.title}
          path={org.path.alias}
          image={org.relationships.field_logo?.localFile?.childImageSharp?.gatsbyImageData}
          orgUrl={org.field_careers_url ? org.field_careers_url.uri : null}
          pageCounter={pageContext.pageCounter || {}}
          nav={true}
          showSearchJobsBtn={hasOpportunities}
        />
        <div className="section-container-full pt-12 pb-16">
          <div className="relative">
            <div className="section-container-2xl">
              <div className="space-y-12 md:space-y-16">{blocks}</div>
            </div>
          </div>
        </div>
      </main>
    </Layout>
  );
};

export default GradsimsTemplate;

export const query = graphql`
  query ($orgId: String!) {
    site {
      siteMetadata {
        brandName
      }
    }
    organisation(id: { eq: $orgId }) {
      id
      drupal_id
      title
      path {
        alias
      }
      relationships {
        ...OrganisationHeaderInfo
      }
      ...OrganisationMetaTag
    }
    inPartnership: allArticle(
      filter: {
        inPartnership: { eq: $orgId }
        field_categories: { elemMatch: { drupal_internal__target_id: { eq: "9dc6046d-cff0-41a8-2303-2327ef970c36" } } }
      }
    ) {
      nodes {
        ...ArticleCard
      }
    }
    allOpportunity(
      filter: { relationships: { field_organisation: { id: { eq: $orgId } } }, field_application_open: { eq: "1" } }
    ) {
      nodes {
        ...OpportunityCard
      }
    }
    headerBackground: file(relativePath: { eq: "global/gradsims-header.png" }) {
      childImageSharp {
        ...HeaderImage
      }
    }
  }
`;
